/**
 * ENTITY REGISTRY — Registro centrale dei tipi entita' per il sistema Relationship.
 *
 * Singleton popolato al boot:
 * 1. loadPlugins() registra i tipi dalle manifest.entityTypes[] dei plugin
 * 2. registerCoreEntityTypes() registra Person, Business, Task, Message, Queue
 *
 * Read-only dopo il bootstrap. Usato da:
 * - RelationPanel: risolvere nomi e link delle entita' correlate
 * - AddRelationDrawer: elencare tipi disponibili e cercare entita'
 * - OggettiPage: mostrare la mappa degli oggetti del sistema
 */

import type { EntityTypeDescriptor } from '@crm/contracts';

/**
 * Ciò che le funzioni sulle etichette chiedono a `t`: tradurre una chiave, niente di più. Non il
 * `TFunction` di i18next, perché le colonne delle griglie ricevono un `t` tipizzato più stretto, che
 * a quello non è assegnabile.
 */
type Translate = (key: string) => string;

const registry = new Map<string, EntityTypeDescriptor>();

export function registerEntityType(descriptor: EntityTypeDescriptor): void {
  if (registry.has(descriptor.typeName)) {
    throw new Error(
      `[EntityRegistry] Tipo "${descriptor.typeName}" gia' registrato.`
    );
  }
  registry.set(descriptor.typeName, descriptor);
}

export function getEntityType(typeName: string): EntityTypeDescriptor | undefined {
  return registry.get(typeName);
}

export function getAllEntityTypes(): EntityTypeDescriptor[] {
  return Array.from(registry.values());
}

export function resolveEntityLabel(typeName: string, id: number): Promise<string> {
  const desc = registry.get(typeName);
  if (!desc) return Promise.resolve(`[${typeName}#${id}]`);
  return desc.resolveLabel(id);
}

/**
 * Versione sincrona: risolve la label dal payload gia' in mano.
 * Ritorna null se il descriptor non espone getLabelFromPayload,
 * cosi' il chiamante puo' cadere su resolveEntityLabel().
 * Usato dai picker di entita' (AddRelationDrawer, TaskForm, TaskEditDrawer)
 * per evitare N+1 quando la lista arriva gia' dal searchEndpoint.
 */
export function getEntityLabelFromPayload(
  typeName: string,
  entity: Record<string, unknown>,
): string | null {
  const desc = registry.get(typeName);
  if (!desc?.getLabelFromPayload) return null;
  return desc.getLabelFromPayload(entity);
}

/**
 * La seconda riga facoltativa di un record nei selettori, dal payload gia' in mano.
 * Ritorna undefined se il descriptor non la espone o non ha niente da dire.
 */
export function getEntityDescriptionFromPayload(
  typeName: string,
  entity: Record<string, unknown>,
): string | undefined {
  return registry.get(typeName)?.getDescriptionFromPayload?.(entity) || undefined;
}

export function getEntityDetailPath(typeName: string, id: number): string {
  const desc = registry.get(typeName);
  if (!desc) return '#';
  return desc.detailPath(id);
}

/** Ritorna il FQN Java associato al typeName corto. Usato al boundary REST. */
export function getEntityFqn(typeName: string): string | undefined {
  return registry.get(typeName)?.fqn;
}

/** Inverso di getEntityFqn: risolve il typeName corto partendo dal FQN ricevuto dal backend. */
export function getEntityShortNameByFqn(fqn: string): string | undefined {
  for (const desc of registry.values()) {
    if (desc.fqn === fqn) return desc.typeName;
  }
  return undefined;
}

/**
 * Etichetta di dominio di un tipo a partire dal suo FQN
 * (es. it.dvel.crm.core.model.Person -> "Contatto").
 *
 * Ripiega sull'ultimo segmento della classe se il tipo non e' registrato: succede con l'entita' di
 * un plugin spento o non caricato, e in quel caso mostrare "Dossier" e' comunque meglio di una cella
 * vuota, che si leggerebbe come "questo record non ha un tipo".
 *
 * Nata privata in AutomationList ed estratta qui quando anche gli elenchi di pipeline e bacheche
 * hanno dovuto mostrare il tipo (TS-117): tre copie della stessa riga sarebbero divergute.
 */
export function getEntityLabelByFqn(fqn: string, t: Translate): string {
  const typeName = getEntityShortNameByFqn(fqn);

  return entityTypeLabel(typeName ? registry.get(typeName) : undefined, t,
    fqn.split('.').pop() ?? fqn);
}

/**
 * Il nome SINGOLARE di un tipo, tradotto: «Contatto» / «Contact».
 *
 * Il descrittore porta il nome solo come chiave i18n (`labelKey`): prima lo portava anche come
 * stringa italiana, e la regola «la chiave se c'è, altrimenti la stringa» riscritta a mano in ogni
 * componente veniva dimenticata, mostrando l'italiano in en_US. Qui resta da decidere solo cosa
 * mostrare quando il tipo non è registrato.
 *
 * @param fallback ciò che si mostra se il tipo non è registrato (plugin spento o non caricato):
 *                 di solito il nome tecnico, meglio di una cella vuota
 */
export function entityTypeLabel(
  et: EntityTypeDescriptor | undefined,
  t: Translate,
  fallback: string,
): string {
  return et ? t(et.labelKey) : fallback;
}

/** Il nome PLURALE di un tipo, tradotto: «Contatti» / «Contacts». Vedi {@link entityTypeLabel}. */
export function entityTypeLabelPlural(
  et: EntityTypeDescriptor | undefined,
  t: Translate,
  fallback: string,
): string {
  return et ? t(et.labelPluralKey) : fallback;
}

/**
 * Convenzione: ogni entita' espone l'id nel campo "<typeName>Id" con
 * iniziale minuscola (person -> personId, quotation -> quotationId, ...).
 * Questo helper estrae l'id dal payload generico; ritorna 0 se mancante
 * (sentinel coerente con il resto del codice, meglio di NaN).
 */
export function extractEntityIdFromPayload(
  entity: Record<string, unknown>,
  typeName: string,
): number {
  const key = `${typeName.charAt(0).toLowerCase()}${typeName.slice(1)}Id`;
  return (entity[key] as number) ?? 0;
}
